import { useState, useEffect } from 'react';
import { getPublicSettings } from '../utils/settings.utils';

interface PublicSettings {
  ALLOW_PUBLIC_REGISTRATION: boolean;
  SITE_NAME: string;
  FRONTEND_URL?: string;
  UI_PRIMARY_COLOR?: string;
  UI_SECONDARY_COLOR?: string;
  UI_ERROR_COLOR?: string;
  UI_WARNING_COLOR?: string;
  UI_SUCCESS_COLOR?: string;
  UI_INFO_COLOR?: string;
  TIME_FORMAT_24_HOUR?: boolean;
}

interface UsePublicSettingsResult {
  settings: PublicSettings | null;
  loading: boolean;
  error: Error | null;
}

// Cache for public settings to avoid multiple fetches
let publicSettingsCache: PublicSettings | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Hook to fetch and use public settings
 * @returns {UsePublicSettingsResult} Object containing settings, loading state, and error
 */
export function usePublicSettings(): UsePublicSettingsResult {
  const [settings, setSettings] = useState<PublicSettings | null>(publicSettingsCache);
  const [loading, setLoading] = useState<boolean>(!publicSettingsCache);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchSettings = async () => {
      // Check if cache is still valid
      if (publicSettingsCache && Date.now() - cacheTimestamp < CACHE_DURATION) {
        setSettings(publicSettingsCache);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const fetchedSettings = await getPublicSettings();
        
        // Update cache
        publicSettingsCache = fetchedSettings as PublicSettings;
        cacheTimestamp = Date.now();
        
        setSettings(fetchedSettings as PublicSettings);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch public settings:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch public settings'));
        
        // Use defaults if fetch fails
        const defaults: PublicSettings = {
          ALLOW_PUBLIC_REGISTRATION: true,
          SITE_NAME: 'Risk Register',
          UI_PRIMARY_COLOR: '#1976d2',
          UI_SECONDARY_COLOR: '#dc004e',
        };
        setSettings(defaults);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return {
    settings,
    loading,
    error,
  };
}

/**
 * Clear the public settings cache
 * Useful when settings have been updated
 */
export function clearPublicSettingsCache(): void {
  publicSettingsCache = null;
  cacheTimestamp = 0;
}